import { useCallback, useEffect, useState } from "react";
import { request } from "../../api/http";
import { useConfirm } from "../../components/Dialog";
import { Select } from "../../components/FormControls";
import { useAuth } from "../../context/AuthContext";
import { useMarket } from "../../context/MarketContext";
import {
  GENDER_OPTIONS,
  PRODUCT_OPTIONS,
  PromoDto,
  PromoListResponse,
  fmtDate,
  formatDefinition,
  formatRange,
  genderLabel,
  productLabel,
} from "../../types/promos";
import { describeError } from "../Coupons/components/CreateCouponModal";
import CreatePromoModal from "./components/CreatePromoModal";

const STATUS_OPTIONS = [
  { value: "1", label: "Activas" },
  { value: "0", label: "Inactivas" },
];

export default function Promos() {
  const { token } = useAuth();
  const { markets } = useMarket();
  const confirm = useConfirm();

  const [items, setItems] = useState<PromoDto[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState("");
  const [product, setProduct] = useState("");
  const [gender, setGender] = useState("");

  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (product) params.set("product", product);
      if (gender) params.set("gender", gender);
      const qs = params.toString();
      const res = await request<PromoListResponse>(
        `/api/admin/operations/promos${qs ? `?${qs}` : ""}`,
        { token, markets },
      );
      setItems(res.items);
    } catch (err) {
      setError(describeError(err, "No se pudieron cargar las promos"));
    } finally {
      setLoading(false);
    }
  }, [token, markets, status, product, gender]);

  useEffect(() => {
    load();
  }, [load]);

  const total = items?.length ?? 0;

  return (
    <div style={{ padding: "1.5rem" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "1.1rem", color: "#2A3F54" }}>Promos</h1>
        <span style={{ color: "#73879C", fontSize: ".82rem" }}>
          {loading ? "Cargando…" : `${total} promos`}
        </span>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={() => setCreateOpen(true)} style={addBtn}>
          + Nueva promo
        </button>
      </div>

      <div style={{ display: "flex", gap: "0.6rem", marginBottom: "1rem", maxWidth: 640 }}>
        <FilterCell label="Estado">
          <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} placeholder="Todas" />
        </FilterCell>
        <FilterCell label="Producto">
          <Select value={product} onChange={setProduct} options={PRODUCT_OPTIONS} placeholder="Todos" />
        </FilterCell>
        <FilterCell label="Género">
          <Select value={gender} onChange={setGender} options={GENDER_OPTIONS} placeholder="Todos" />
        </FilterCell>
      </div>

      {error && (
        <div
          style={{
            background: "#fde2e2",
            color: "#b32424",
            padding: "0.5rem 0.7rem",
            borderRadius: "0.4rem",
            marginBottom: "1rem",
            fontSize: ".85rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.55rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "auto",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".83rem" }}>
          <thead>
            <tr style={{ background: "#f8f9fb" }}>
              <Th>Nombre</Th>
              <Th>Producto</Th>
              <Th>Definición</Th>
              <Th>Edad</Th>
              <Th>Precio</Th>
              <Th>Género</Th>
              <Th>Vigencia</Th>
              <Th>Estado</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {!loading && items && items.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: "1.5rem", textAlign: "center", color: "#73879C" }}>
                  Sin resultados.
                </td>
              </tr>
            )}
            {items?.map((promo) => (
              <PromoRow
                key={promo.promoId}
                promo={promo}
                token={token}
                confirm={confirm}
                onReload={load}
              />
            ))}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <CreatePromoModal
          token={token}
          onClose={() => setCreateOpen(false)}
          onApplied={() => {
            setCreateOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function PromoRow({
  promo,
  token,
  confirm,
  onReload,
}: {
  promo: PromoDto;
  token: string | null;
  confirm: ReturnType<typeof useConfirm>;
  onReload: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (busy) return;
    const ok = await confirm({
      title: promo.active ? "Desactivar promo" : "Activar promo",
      message: `Se ${promo.active ? "desactivará" : "activará"} la promo "${promo.name}". ¿Continuar?`,
      confirmLabel: promo.active ? "Desactivar" : "Activar",
      tone: promo.active ? "danger" : "primary",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await request(`/api/admin/operations/promos/${promo.promoId}/status`, {
        method: "POST",
        token,
        body: { active: !promo.active },
      });
      onReload();
    } catch {
      setBusy(false);
    }
  };

  return (
    <tr style={{ borderTop: "1px solid #f1f3f5" }}>
      <Td>{promo.name}</Td>
      <Td>{productLabel(promo.product)}</Td>
      <Td>{formatDefinition(promo)}</Td>
      <Td>{formatRange(promo.lowerAge, promo.upperAge)}</Td>
      <Td>{formatRange(promo.lowerPrice, promo.upperPrice)}</Td>
      <Td>{genderLabel(promo.gender)}</Td>
      <Td>{`${fmtDate(promo.startDate)} → ${fmtDate(promo.endDate)}`}</Td>
      <Td>
        <span
          style={{
            display: "inline-block",
            padding: "0.15rem 0.5rem",
            borderRadius: "0.4rem",
            fontSize: ".72rem",
            fontWeight: 600,
            border: "1px solid",
            ...(promo.active
              ? { background: "#d4edda", color: "#155724", borderColor: "#c3e6cb" }
              : { background: "#f1f3f5", color: "#495057", borderColor: "#dee2e6" }),
          }}
        >
          {promo.active ? "Activa" : "Inactiva"}
        </span>
      </Td>
      <Td>
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          style={{
            padding: "0.25rem 0.55rem",
            borderRadius: "0.35rem",
            border: "1px solid #dee2e6",
            background: "white",
            color: "#0069d9",
            fontSize: ".74rem",
            fontWeight: 600,
            cursor: busy ? "default" : "pointer",
            opacity: busy ? 0.5 : 1,
          }}
        >
          {promo.active ? "Desactivar" : "Activar"}
        </button>
      </Td>
    </tr>
  );
}

function FilterCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ flex: 1 }}>
      <div
        style={{
          fontSize: ".68rem",
          color: "#73879C",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: ".3px",
          marginBottom: "0.25rem",
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: "0.6rem 0.7rem",
        textAlign: "left",
        fontWeight: 600,
        color: "#1f2933",
        fontSize: ".72rem",
        textTransform: "uppercase",
        letterSpacing: ".3px",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td style={{ padding: "0.55rem 0.7rem", verticalAlign: "middle", whiteSpace: "nowrap" }}>
      {children}
    </td>
  );
}

const addBtn: React.CSSProperties = {
  padding: "0.45rem 0.9rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};
